import React, { useEffect, useReducer, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { RowCentered, RowForm, SubmitWrapper } from 'pages/Login/style';
import { COMPLETE_NEW_PASSWORD_SUBMIT } from 'redux/sagas/actions/authentication';
import {
  RESET_ERRORS,
  SET_ACCESS_ERROR,
  SET_ACCESS_ERROR_PASSWORD_NOT_MATCH,
  SET_ACCESS_ERROR_PASSWORD_NOT_VALID
} from 'redux/slices/authentication';
import { Button, Input, Tooltip } from 'ui/atoms';
import MainFont from 'ui/typography/mainFont';
import validator from 'utils/regex-validator';

import { ErrorWrapper, ForgotPasswordText, ForgotPasswordTitle } from './style';

const reducer = (state, action) => {
  switch (action.type) {
    case 'add_password':
      return {
        ...state,
        password: action.payload
      };
    case 'add_confirm_password':
      return {
        ...state,
        confirm_password: action.payload
      };
    default:
      return state;
  }
};

const newField = () => '';

const initialState = {
  confirm_password: newField(),
  password: newField()
};

const CreatePassword = ({
  createNewPasswordSubmit,
  setError,
  setErrorPswDontMatch,
  setErrorPswNotValid,
  resetErrors,
  genericError,
  errorPswNotValid,
  errorPswDontMatch
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [formattedPasswordErrors, setFormattedPassordErrors] = useState('');

  const { t } = useTranslation();

  // eslint-disable-next-line consistent-return
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleValidation = () => {
    if (!validator.password(state.password)) {
      setErrorPswNotValid(t('password_not_valid'));
      return false;
    }
    if (
      state.password !== state.confirm_password &&
      state.password.length === state.confirm_password.length
    ) {
      setErrorPswDontMatch(t('password_not_match_try_again'));
      return false;
    }
    resetErrors();
    return true;
  };

  const handleSubmitPasswword = () => {
    if (handleValidation()) {
      createNewPasswordSubmit(state.password);
    }
  };

  const isDisabled = () => !state.password || !state.confirm_password;

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      handleSubmitPasswword();
    }
  };

  useEffect(() => {
    if (state.password) {
      if (!validator.password(state.password)) {
        setErrorPswNotValid(t('password_not_valid'));
      } else {
        setErrorPswNotValid(null);
      }
    }
    if (state.password && state.confirm_password) {
      if (state.password !== state.confirm_password) {
        setErrorPswDontMatch(t('password_not_match_try_again'));
      } else {
        setErrorPswDontMatch(null);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.password, state.confirm_password]);

  useEffect(() => {
    if (errorPswNotValid && errorPswDontMatch) {
      if (errorPswDontMatch.toLowerCase().includes('confirm')) {
        setFormattedPassordErrors(
          '<ul><li>Passwords do not match.</li><li>Confirm password is not valid</li></ul>'
        );
      } else {
        setFormattedPassordErrors(
          '<ul><li>Passwords do not match.</li><li>Password is not valid</li></ul>'
        );
      }
    } else if (errorPswDontMatch && !errorPswNotValid) {
      setFormattedPassordErrors(errorPswDontMatch);
    } else if (errorPswNotValid && !errorPswDontMatch) {
      setFormattedPassordErrors(errorPswNotValid);
    } else if (!errorPswNotValid && !errorPswDontMatch) {
      setFormattedPassordErrors('');
    }
  }, [errorPswNotValid, errorPswDontMatch]);

  return (
    <>
      <ErrorWrapper createPassword>
        {genericError && <MainFont type="errorLabel">{genericError}</MainFont>}
      </ErrorWrapper>
      <ForgotPasswordTitle>
        <MainFont configuration={{ fontSize: 24 }} type="3">
          {t('create_password')}
        </MainFont>
      </ForgotPasswordTitle>
      <ForgotPasswordText>
        <MainFont configuration={{ fontSize: 14 }} type="3">
          {t('create_password_text')}
        </MainFont>
      </ForgotPasswordText>
      <div onKeyPress={handleKeyPress}>
        <RowForm>
          <Input
            type="password"
            placeholder={`${t('password')}`}
            variant="login"
            value={state.password}
            status={formattedPasswordErrors ? 'invalid' : ''}
            onChange={e =>
              dispatch({
                type: 'add_password',
                payload: e.currentTarget.value
              })
            }
            isShowPassword
            data-tip={`${formattedPasswordErrors}`}
            data-for="psw-tooltip"
            data-html
          />
          {formattedPasswordErrors && <Tooltip id="psw-tooltip" error />}
        </RowForm>
        <RowForm>
          <Input
            type="password"
            placeholder={`${t('confirm_password')}`}
            variant="login"
            value={state.confirm_password}
            status={formattedPasswordErrors ? 'invalid' : ''}
            onChange={e =>
              dispatch({
                type: 'add_confirm_password',
                payload: e.currentTarget.value
              })
            }
            isShowPassword
          />
        </RowForm>
      </div>
      <RowCentered>
        <SubmitWrapper>
          <Button
            variant="login"
            disabled={isDisabled()}
            status={isDisabled()}
            onClick={isDisabled() ? () => null : handleSubmitPasswword}
          >
            {t('submit')}
          </Button>
        </SubmitWrapper>
      </RowCentered>
    </>
  );
};

CreatePassword.propTypes = {
  createNewPasswordSubmit: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setErrorPswDontMatch: PropTypes.func,
  setErrorPswNotValid: PropTypes.func,
  resetErrors: PropTypes.func,
  genericError: PropTypes.string,
  errorPswDontMatch: PropTypes.string,
  errorPswNotValid: PropTypes.string
};

export default connect(
  ({ authentication }) => ({
    genericError: authentication.errorMessage,
    errorPswDontMatch: authentication.errorPswDontMatch,
    errorPswNotValid: authentication.errorPswNotValid
  }),
  dispatch => ({
    setError: payload => dispatch(SET_ACCESS_ERROR(payload)),
    createNewPasswordSubmit: payload =>
      dispatch({ type: COMPLETE_NEW_PASSWORD_SUBMIT._REQUEST, payload }),
    setErrorPswDontMatch: payload =>
      dispatch(SET_ACCESS_ERROR_PASSWORD_NOT_MATCH(payload)),
    setErrorPswNotValid: payload =>
      dispatch(SET_ACCESS_ERROR_PASSWORD_NOT_VALID(payload)),
    resetErrors: () => dispatch(RESET_ERRORS())
  })
)(CreatePassword);
